import type { AxiosInstance } from 'axios';
import queryString from 'query-string';
import type * as ChatApi from './interfaces/chat';
import {
  prepareContextData,
  stringifyContextData,
  transformChatInstance,
  transformChatFileUpload,
  transformChatQuery,
} from './transformers';

export class ChatBaseClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  submitChatQuery = async (data: ChatApi.SubmitChatQuery.Request) => {
    const postData = {
      ...data,
      context: prepareContextData(data.context),
    };
    const res = await this.authenticatedClient.post<ChatApi.SubmitChatQuery.Response>('/chat/submit-query', postData);
    return {
      identifier: res.data.identifier,
    };
  };

  regenerateQuery = async (data: ChatApi.RegenerateQuery.Request) => {
    const postData = {
      ...data,
      context: prepareContextData(data.context),
    };
    const res = await this.authenticatedClient.post<ChatApi.RegenerateQuery.Response>(`/chat/regenerate-query`, postData);
    return {
      identifier: res.data.identifier,
    };
  };

  getChatHistory = async (data: ChatApi.GetChatHistory.Request): Promise<ChatApi.GetChatHistory.Response<Date>> => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify({
      cursor: data.cursor,
      pageSize: data.pageSize,
      context,
      mode: data.mode,
      includeAdditional: data.includeAdditional,
      brandIdentifier: data.brandIdentifier,
      isBookmaked: data.isBookmarked,
      name: data.name,
    }, {
      arrayFormat: 'bracket',
      skipNull: true,
    });

    const res = await this.authenticatedClient.get<ChatApi.GetChatHistory.Response>(`/chat/history?${qs}`);

    return {
      hasMore: res.data.hasMore,
      items: res.data.items.map(transformChatInstance),
      shared: res.data.shared.map(transformChatInstance),
    };
  };

  searchChatHistory = async (data: ChatApi.SearchChatHistory.Request): Promise<ChatApi.SearchChatHistory.Response<Date>> => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify({
      cursor: data.cursor,
      pageSize: data.pageSize,
      context,
      mode: data.mode,
      includeAdditional: data.includeAdditional,
      brandIdentifier: data.brandIdentifier,
      name: data.name,
      isBookmarked: data.isBookmarked,
    }, {
      arrayFormat: 'bracket',
      skipNull: true,
    });

    const res = await this.authenticatedClient.get<ChatApi.SearchChatHistory.Response>(`/chat/history/search?${qs}`);

    return {
      hasMore: res.data.hasMore,
      items: res.data.items.map(transformChatInstance),
      shared: res.data.shared.map(transformChatInstance),
    };
  };

  getChatInitialMessage = async (data: ChatApi.GetChatInitialMessage.Request) => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify({
      context,
      brandIdentifier: data.brandIdentifier,
    }, {
      skipNull: true,
    });

    const res = await this.authenticatedClient.get<ChatApi.GetChatInitialMessage.Response>(`/chat/initial-message?${qs}`);
    return res.data;
  };

  fileUploadPreCheck = async ({ chatInstanceIdentifier, ...data }: ChatApi.FileUploadPreCheck.Request): Promise<ChatApi.FileUploadPreCheck.Response> => {
    const res = await this.authenticatedClient.post<ChatApi.FileUploadPreCheck.Response>(`/chat/${chatInstanceIdentifier}/file-upload-pre-check`, data);
    return res.data;
  };

  uploadChatFiles = async (data: ChatApi.UploadFiles.Request): Promise<ChatApi.UploadFiles.Response> => {
    const postData = {
      ...data,
      context: prepareContextData(data.context),
    };
    const res = await this.authenticatedClient.post<ChatApi.UploadFiles.Response<string>>('/chat/upload-documents', postData);
    return {
      chatInstance: transformChatInstance(res.data.chatInstance),
      fileUpload: transformChatFileUpload(res.data.fileUpload),
    };
  };

  lookupContext = async (data: ChatApi.LookupContext.Request) => {
    const qs = queryString.stringify({
      contextType: data.contextType,
      entityId: data.entityId,
      objectId: data.objectId,
    });

    const res = await this.authenticatedClient.get<ChatApi.LookupContext.Response>(`/chat/context?${qs}`);
    return res.data;
  };

  getChatContextAccess = async (data: ChatApi.GetChatContextAccess.Request) => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify({
      context,
    }, {
      skipNull: true,
    });

    const res = await this.authenticatedClient.get<ChatApi.GetChatContextAccess.Response>(`/chat/context-access?${qs}`);
    return res.data;
  };

  searchChatFilters = async (data: ChatApi.SearchChatFilters.Request) => {
    const res = await this.authenticatedClient.get<ChatApi.SearchChatFilters.Response>(`/chat/filters/search?${queryString.stringify(data, { arrayFormat: 'bracket' })}`);
    return res.data;
  };

  searchChatProjects = async (data: ChatApi.SearchChatProjects.Request) => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify({
      context,
      query: data.query,
    }, {
      skipNull: true,
    });

    const res = await this.authenticatedClient.get<ChatApi.SearchChatProjects.Response>(`/chat/filters/projects?${qs}`);
    return res.data;
  };

  getPendingQuery = async (data: ChatApi.GetPendingQuery.Request) => {
    return this.authenticatedClient.get<ChatApi.GetPendingQuery.Response>(`/chat/pending-query?queryIdentifier=${data.queryIdentifier}`)
      .then(res => ({
        chatInstance: transformChatInstance(res.data.chatInstance),
        query: transformChatQuery(res.data.query),
      }));
  };

  contextSearch = ({ type, q, contextIdentifier }: ChatApi.ContextSearch.Request) => {
    return this.authenticatedClient.get<ChatApi.ContextSearch.Response>(`/chat/context-search?${queryString.stringify({ type, q, contextIdentifier })}`);
  };

  searchTags = async (data: ChatApi.SearchTags.Request) => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify({
      context,
      query: data.query,
    }, {
      skipNull: true,
    });

    return this.authenticatedClient.get<ChatApi.SearchTags.Response>(`/tags?${qs}`);
  };

  submitChatAction = async (data: ChatApi.SubmitChatAction.Request) => {
    return this.authenticatedClient.post<ChatApi.SubmitChatAction.Response>('/chat/actions', data);
  };

  sendHcpSchedulingEmail = async (data: ChatApi.SendHcpSchedulingEmail.Request) => {
    return this.authenticatedClient.post<ChatApi.SendHcpSchedulingEmail.Response>('/chat/send-hcp-scheduling-email', data)
      .then(res => ({
        query: transformChatQuery(res.data.query),
      }));
  };

  getChatContexts = async (data: ChatApi.GetChatContexts.Request): Promise<ChatApi.GetChatContexts.Response> => {
    const qs = queryString.stringify({
      brandIdentifier: data.brandIdentifier,
      ...(data.applicationIdentifier && { applicationIdentifier: data.applicationIdentifier }),
    });

    const res = await this.authenticatedClient.get<ChatApi.GetChatContexts.Response>(`/chat/contexts?${qs}`);
    return res.data;
  };

  getChatQuestions = async (data: ChatApi.GetChatQuestions.Request): Promise<ChatApi.GetChatQuestions.Response> => {
    const qs = queryString.stringify({
      brandIdentifier: data.brandIdentifier,
    });

    const res = await this.authenticatedClient.get<ChatApi.GetChatQuestions.Response>(`/chat/questions?${qs}`);
    return res.data;
  };

  getChatLandingCards = async (data: ChatApi.GetChatLandingCards.Request): Promise<ChatApi.GetChatLandingCards.Response> => {
    const qs = queryString.stringify({
      brandIdentifier: data.brandIdentifier,
      applicationIdentifier: data.applicationIdentifier,
    });

    const res = await this.authenticatedClient.get<ChatApi.GetChatLandingCards.Response>(`/chat/landing-cards?${qs}`);
    return res.data;
  };

  fetchLoadingAudioClip = async (): Promise<string> => {
    const response = await this.authenticatedClient.get('/chat/generate-loading-audio-clip');
    if (response.data.audioUrl) {
      return response.data.audioUrl;
    }
    throw new Error('Invalid audio data received');
  };
}